import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import Link from "next/link";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary: "",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({ className, variant, size, asChild = false, href, children, ...props }) {
  const Comp = asChild ? Slot : "button";

  if (variant === "secondary") {
    const secondaryClasses = cn(
      "inline-flex items-center justify-between gap-4 whitespace-nowrap cursor-pointer",
      "bg-[#1E1E1E] border border-[rgba(96,96,96,0.4)] rounded-[10px]",
      "shadow-[0px_0px_13.3px_7px_#1A1A1A,inset_0px_0px_0px_0px_transparent]",
      "hover:border-[#606060]",
      "hover:shadow-[0px_0px_0px_0px_transparent,inset_0px_0px_8.7px_1px_#bfbeb8]",
      "transition-[box-shadow,border-color] duration-500 ease-in-out",
      className
    );

    const secondaryStyle = {
      padding: "16px 16px 18px",
      height: "54px",
    };

    const content = (
      <>
        <span
          style={{
            fontWeight: 500,
            fontSize: "16px",
            lineHeight: "20px",
            letterSpacing: "-0.009em",
            color: "#BFBEB8",
          }}
        >
          {children}
        </span>
        <svg width="15" height="10" viewBox="0 0 15 10" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
          <path d="M13.25 4.9165L0.75 4.9165" stroke="#BFBEB8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M9.91663 0.75L13.3762 4.20956C13.7095 4.54289 13.8762 4.70956 13.8762 4.91667C13.8762 5.12377 13.7095 5.29044 13.3762 5.62377L9.91663 9.08333" stroke="#BFBEB8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </>
    );

    if (href) {
      return (
        <Link href={href} className={secondaryClasses} style={secondaryStyle} {...props}>
          {content}
        </Link>
      );
    }

    return (
      <button data-slot="button" className={secondaryClasses} style={secondaryStyle} {...props}>
        {content}
      </button>
    );
  }

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {children}
    </Comp>
  );
}

export { Button, buttonVariants };
